import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { EmailSendModal } from '../../components/marketing/EmailSendModal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'
import { ChannelSelect } from '../../components/ChannelSelect'

type Status = 'planned' | 'active' | 'paused' | 'done' | 'cancelled'

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  active: boolean
}

interface Campaign {
  id: number
  b4lId: string | null
  name: string
  channel: string
  products: { id: number; sku: string; name: string; brand: string }[]
  discountPct: string
  budget: string
  startDate: string
  endDate: string
  partnerName: string | null
  partnerEmail: string | null
  notes: string | null
  status: Status
}

const STATUS_LABELS: Record<Status, string> = {
  planned: 'Planificată',
  active: 'Activă',
  paused: 'Oprită temporar',
  done: 'Finalizată',
  cancelled: 'Anulată',
}

const STATUS_COLORS: Record<Status, string> = {
  planned: 'bg-b4l-bg text-b4l-text-muted',
  active: 'bg-b4l-green-light text-b4l-green-dark',
  paused: 'bg-b4l-amber-light text-b4l-amber-dark',
  done: 'bg-b4l-blue-light text-b4l-blue-dark',
  cancelled: 'bg-b4l-red-light text-b4l-red-dark',
}

export function CampaniiPromoPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.campaigns:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Campaign | null>(null)
  const [sendTarget, setSendTarget] = useState<Campaign | null>(null)

  const query = useQuery<{ campaigns: Campaign[] }>({
    queryKey: ['campaigns-simple'],
    queryFn: async () =>
      (await api.get<{ campaigns: Campaign[] }>('/marketing/campaigns-simple')).data,
  })

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Campaign>) =>
      (await api.post('/marketing/campaigns-simple', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-simple'] })
      toast.success('Campanie adăugată')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Campaign> }) =>
      (await api.patch(`/marketing/campaigns-simple/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-simple'] })
      toast.success('Salvat')
    },
    onError: showError,
  })

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/marketing/campaigns-simple/${id}/status`, { status })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-simple'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/campaigns-simple/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-simple'] })
      toast.success('Șters')
    },
    onError: showError,
  })

  const campaigns = useMemo(() => query.data?.campaigns ?? [], [query.data])
  const pag = useClientPagination(campaigns, 10)
  const products = useMemo(
    () => (productsQuery.data?.products ?? []).filter((p) => p.active),
    [productsQuery.data],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Campanii promo simple</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Campanie nouă</Button>}
      </div>

      <Card description="Campanii promo cu discount procentual pe produs și partener. Partenerul primește automat un email de notificare cu referința B4L-CAMP-XXXXXX. Pentru fluxul complet — calcul P&L (profit și pierdere) și export CSV/XLSX — folosește pagina „Campanii promo - flux”.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : campaigns.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio campanie. {canWrite && 'Apasă "+ Campanie nouă".'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">ID B4L</th>
                  <th className="py-2 pr-3 font-medium">Denumire</th>
                  <th className="py-2 pr-3 font-medium">Canal</th>
                  <th className="py-2 pr-3 font-medium">Produs</th>
                  <th className="py-2 pr-3 font-medium text-right">Discount</th>
                  <th className="py-2 pr-3 font-medium text-right">Buget</th>
                  <th className="py-2 pr-3 font-medium">Perioadă</th>
                  <th className="py-2 pr-3 font-medium">Partener</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  <th className="py-2 pr-3 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((c) => (
                  <tr key={c.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-mono text-[11px] text-b4l-purple-dark">
                      {c.b4lId ?? '—'}
                    </td>
                    <td className="py-2 pr-3 max-w-[180px] truncate" title={c.name}>
                      {c.name}
                    </td>
                    <td className="py-2 pr-3 text-xs">{c.channel}</td>
                    <td className="py-2 pr-3 text-xs">
                      {c.products.length === 0 ? (
                        <span className="text-b4l-text-muted">—</span>
                      ) : (
                        <span title={c.products.map((p) => `${p.sku} — ${p.name}`).join('\n')}>
                          <span className="font-mono">{c.products[0].sku}</span>
                          {c.products.length > 1 && (
                            <span className="ml-1 inline-block rounded bg-b4l-bg px-1.5 py-0.5 text-[10px] text-b4l-text-muted">
                              +{c.products.length - 1}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">
                      {Number(c.discountPct).toFixed(0)}%
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">
                      {Number(c.budget).toFixed(0)}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {new Date(c.startDate).toLocaleDateString('en-GB')}
                      {' - '}
                      {new Date(c.endDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 pr-3 text-xs">{c.partnerName ?? '—'}</td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={c.status}
                          onChange={(e) =>
                            updateStatusMutation.mutate({
                              id: c.id,
                              status: e.target.value as Status,
                            })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {STATUS_LABELS[c.status]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        {canWrite &&
                          c.partnerEmail &&
                          c.status !== 'cancelled' &&
                          c.status !== 'done' && (
                            <Button
                              onClick={() => setSendTarget(c)}
                              className="!px-2 !py-1 !text-xs"
                            >
                              {c.b4lId ? 'Re-trimite' : 'Notifică'}
                            </Button>
                          )}
                        {canWrite && (
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(c)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                        )}
                        {canWrite && !c.b4lId && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi "${c.name}"?`)) deleteMutation.mutate(c.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="campanii"
          />
        </div>
      </Card>

      {createOpen && (
        <CampaignFormModal
          title="Campanie nouă"
          submitLabel="Adaugă"
          products={products}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <CampaignFormModal
          title={`Editează ${editTarget.name}`}
          submitLabel="Salvează"
          products={products}
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) =>
            updateMutation.mutate(
              { id: editTarget.id, data },
              { onSuccess: () => setEditTarget(null) },
            )
          }
          submitting={updateMutation.isPending}
        />
      )}

      {sendTarget && (
        <EmailSendModal
          baseEndpoint={`/marketing/campaigns-simple/${sendTarget.id}`}
          previewSubpath="/email-preview"
          sendSubpath="/send-notification"
          title={`Notificare către ${sendTarget.partnerEmail}`}
          b4lPlaceholder="[B4L-CAMP-XXXXXX]"
          onClose={() => setSendTarget(null)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['campaigns-simple'] })
            queryClient.invalidateQueries({ queryKey: ['dashboard'] })
            setSendTarget(null)
          }}
        />
      )}
    </div>
  )
}

interface FormProps {
  title: string
  submitLabel: string
  products: Product[]
  initial?: Campaign
  onClose: () => void
  onSubmit: (data: Partial<Campaign>) => void
  submitting: boolean
}

function CampaignFormModal({
  title,
  submitLabel,
  products,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [channel, setChannel] = useState(initial?.channel ?? 'boost4life.ro')
  const [productIds, setProductIds] = useState<number[]>(
    initial?.products ? initial.products.map((p) => p.id) : [],
  )
  const [productSearch, setProductSearch] = useState('')
  const [discountPct, setDiscountPct] = useState(
    initial?.discountPct ? String(initial.discountPct) : '15',
  )
  const [budget, setBudget] = useState(initial?.budget ? String(initial.budget) : '')
  const [startDate, setStartDate] = useState(
    initial?.startDate ? initial.startDate.slice(0, 10) : '',
  )
  const [endDate, setEndDate] = useState(initial?.endDate ? initial.endDate.slice(0, 10) : '')
  const [partnerName, setPartnerName] = useState(initial?.partnerName ?? '')
  const [partnerEmail, setPartnerEmail] = useState(initial?.partnerEmail ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase()
    if (!q) return products
    return products.filter(
      (p) =>
        p.sku.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q),
    )
  }, [products, productSearch])

  const productMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])

  const toggleProduct = (id: number) =>
    setProductIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const submit = () => {
    if (
      !name.trim() ||
      !channel.trim() ||
      productIds.length === 0 ||
      !budget ||
      !startDate ||
      !endDate
    ) {
      toast.error('Completează nume, canal, cel puțin un produs, buget și perioada')
      return
    }
    onSubmit({
      name: name.trim(),
      channel: channel.trim(),
      productIds,
      discountPct: Number(discountPct),
      budget: Number(budget),
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      partnerName: partnerName.trim() || null,
      partnerEmail: partnerEmail.trim() || null,
      notes: notes.trim() || null,
    } as unknown as Partial<Campaign>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Denumire campanie" value={name} onChange={(e) => setName(e.target.value)} />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* === Coloana stanga: detalii campanie === */}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Canal</label>
              <ChannelSelect value={channel} onChange={setChannel} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Discount %"
                type="number"
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
              />
              <Field
                label="Buget (RON)"
                type="number"
                step="0.01"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Început"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Field
                label="Sfârșit"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
              <p className="mb-2 text-[11px] text-b4l-text-muted">
                Partenerul primește e-mailul cu <span className="font-mono">B4L-CAMP-XXXXXX</span>{' '}
                (manager canal, influencer, agenție). Lasă gol dacă e campanie internă.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Partener (nume)"
                  value={partnerName ?? ''}
                  onChange={(e) => setPartnerName(e.target.value)}
                />
                <Field
                  label="Email partener"
                  type="email"
                  value={partnerEmail ?? ''}
                  onChange={(e) => setPartnerEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Note</label>
              <textarea
                value={notes ?? ''}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* === Coloana dreapta: produse === */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-medium text-b4l-text-muted">
                Produse ({productIds.length} selectate)
              </label>
              {productIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setProductIds([])}
                  className="text-[11px] text-b4l-text-muted hover:text-b4l-text"
                >
                  Curăță
                </button>
              )}
            </div>
            {productIds.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {productIds.map((id) => {
                  const p = productMap.get(id)
                  if (!p) return null
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded bg-b4l-blue-light px-2 py-0.5 text-[11px] text-b4l-blue-dark"
                    >
                      <span className="font-mono">{p.sku}</span>
                      <button
                        type="button"
                        onClick={() => toggleProduct(id)}
                        className="ml-0.5 text-b4l-blue-dark/70 hover:text-b4l-blue-dark"
                        aria-label={`Elimină ${p.sku}`}
                      >
                        ×
                      </button>
                    </span>
                  )
                })}
              </div>
            )}
            <input
              type="text"
              placeholder="Caută produs (SKU / nume / brand)..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="mb-1 w-full rounded border border-b4l-border px-3 py-1.5 text-sm"
            />
            <div className="max-h-[420px] overflow-y-auto rounded border border-b4l-border">
              {filteredProducts.length === 0 ? (
                <div className="p-3 text-center text-xs text-b4l-text-muted">
                  Niciun produs găsit
                </div>
              ) : (
                filteredProducts.map((p) => {
                  const checked = productIds.includes(p.id)
                  return (
                    <label
                      key={p.id}
                      className={`flex cursor-pointer items-center gap-2 border-b border-b4l-border px-2 py-1.5 text-sm last:border-0 hover:bg-b4l-bg-soft ${
                        checked ? 'bg-b4l-blue-light/40' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleProduct(p.id)}
                      />
                      <span className="font-mono text-xs">{p.sku}</span>
                      <span className="truncate">{p.name}</span>
                      <span className="ml-auto text-[10px] text-b4l-text-muted">{p.brand}</span>
                    </label>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
